import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { useSavedPlaces } from '../contexts/SavedPlacesContext';
import { PlaceItem } from '../types/explore';
import { getImageVariants } from '../utils/imageUtils';
import { usePermissions } from '../hooks/usePermissions';
import RegistrationPrompt from './RegistrationPrompt';

interface ChatPlaceCardProps {
  'data-id': string;
  baseline_id?: string;
  title: string;
  city?: string;
  location_link?: string;
  rating?: number;
  description: string;
  photos: string[];
  geo?: { lat: number; lng: number };
  variant: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onTouch?: () => void;
  highlighted: boolean;
  hovered?: boolean; // For hover highlighting (visual only, no centering)
  className?: string;
  // Additional fields for Firestore compatibility
  image_url?: string;
  type?: string;
  // Full result object for saving (contains all 67 fields)
  fullResult?: any;
  // Mobile overlay control
  showOverlay?: boolean;
  onOverlayClick?: () => void;
}

const ChatPlaceCard: React.FC<ChatPlaceCardProps> = ({
  'data-id': dataId,
  baseline_id,
  title,
  city,
  location_link,
  rating,
  description,
  photos,
  geo,
  variant,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onDoubleClick,
  onTouch,
  highlighted,
  hovered,
  className = "",
  image_url,
  type,
  fullResult,
  showOverlay,
  onOverlayClick
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const { isPlaceSaved, savePlace, unsavePlace} = useSavedPlaces();
  const { canSave } = usePermissions();
  
  const placeId = baseline_id || dataId;
  const isSaved = isPlaceSaved(placeId);

  // Handle save/unsave functionality
  const handleSaveClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Check permissions before saving (unsaving is always allowed)
    if (!isSaved && !canSave().allowed) {
      setShowRegistrationPrompt(true);
      return;
    }
    
    setIsAnimating(true);
    
    try {
      if (isSaved) {
        await unsavePlace(placeId);
      } else {
        // If fullResult is provided, use it (contains all 67 fields)
        // Otherwise fall back to limited placeItem
        if (fullResult) {
          // IMPORTANT: Override the id with placeId (baseline_id) to ensure consistency
          const placeToSave = { ...fullResult, id: placeId } as PlaceItem;
          await savePlace(placeToSave);
        } else {
          // Fallback: create limited PlaceItem
          const placeItem: PlaceItem = {
            id: placeId,
            title,
            city: city || undefined,
            image_url: image_url || photos[0] || '',
            rating: rating || undefined,
            type: (type as 'hotel' | 'food' | 'attraction' | 'experience') || 'attraction',
            photo_1_cdn: photos[0] || undefined,
            photo_2_cdn: photos[1] || undefined,
            photo_3_cdn: photos[2] || undefined,
            photo_4_cdn: photos[3] || undefined,
            photo_5_cdn: photos[4] || undefined,
            geo_point: geo ? { lat: geo.lat, lon: geo.lng } : undefined,
            ...(type === 'hotel' && { stay_description: description }),
            ...(type === 'food' && { food_description: description }),
            ...(type === 'attraction' && { cultural_significance: description }),
            ...(type === 'experience' && { cultural_significance: description }),
            location_link: location_link || undefined
          };
          await savePlace(placeItem);
        }
      }
    } catch (error) {
      // Silently handle error
    } finally {
      // Keep animation for a bit, then stop
      setTimeout(() => {
        setIsAnimating(false);
      }, 400);
    }
  };

  const handleSaveTouch = async (e: React.TouchEvent) => {
    e.stopPropagation();
    
    // Check permissions before saving (unsaving is always allowed)
    if (!isSaved && !canSave().allowed) {
      setShowRegistrationPrompt(true);
      return;
    }
    
    setIsAnimating(true);
    
    try {
      if (isSaved) {
        await unsavePlace(placeId);
      } else {
        if (fullResult) {
          await savePlace(fullResult as PlaceItem);
        } else {
          const placeItem: PlaceItem = {
            id: placeId,
            title,
            city: city || undefined,
            image_url: image_url || photos[0] || '',
            rating: rating || undefined,
            type: (type as 'hotel' | 'food' | 'attraction' | 'experience') || 'attraction',
            photo_1_cdn: photos[0] || undefined,
            photo_2_cdn: photos[1] || undefined,
            photo_3_cdn: photos[2] || undefined,
            photo_4_cdn: photos[3] || undefined,
            photo_5_cdn: photos[4] || undefined,
            geo_point: geo ? { lat: geo.lat, lon: geo.lng } : undefined,
            ...(type === 'hotel' && { stay_description: description }),
            ...(type === 'food' && { food_description: description }),
            ...(type === 'attraction' && { cultural_significance: description }),
            ...(type === 'experience' && { cultural_significance: description }),
            location_link: location_link || undefined
          };
          await savePlace(placeItem);
        }
      }
    } catch (error) {
      // Silently handle error
    } finally {
      setTimeout(() => {
        setIsAnimating(false);
      }, 600);
    }
  };

  // Handle double-click detection (desktop)
  const handleCardClick = (e: React.MouseEvent) => {
    // Always trigger single-click action immediately for responsiveness
    onClick?.();
    
    // Check for double-click
    if (clickTimeout) {
      // This is a double-click - clear timeout and trigger double-click action
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      onDoubleClick?.();
    } else {
      // First click - set timeout to detect double-click
      const timeout = setTimeout(() => {
        setClickTimeout(null);
      }, 300);
      setClickTimeout(timeout);
    }
  };

  // Handle touch for mobile (first touch highlights marker, shows overlay)
  const handleTouchStartCard = (e: React.TouchEvent) => {
    // Record the initial touch position
    const touch = e.touches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const handleTouchEndCard = (e: React.TouchEvent) => {
    // Calculate distance moved
    if (!touchStart) return;
    
    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStart.x);
    const deltaY = Math.abs(touch.clientY - touchStart.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Only trigger tap if movement was less than 10px (indicating a tap, not a scroll)
    if (distance < 10) {
      onTouch?.();
    }
    
    // Reset touch start position
    setTouchStart(null);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onDoubleClick) {
      e.preventDefault();
      onDoubleClick();
    }
  };

  const getTypeIcon = (entityType?: string) => {
    // Use the entity_type prop directly from backend
    switch (entityType) {
      case 'hotel':
        return 'H';
      case 'food':
        return 'F';
      case 'attraction':
        return 'A';
      default:
        return 'A'; // Default to attraction if not specified
    }
  };

  const getTypeColor = (icon: string) => {
    switch (icon) {
      case 'F': return 'bg-orange-100 text-orange-800';
      case 'H': return 'bg-blue-100 text-blue-800';
      case 'A': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const truncateTitle = (title: string | undefined, maxLength: number = 20) => {
    if (!title) return '';
    return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;
  };

  const getImageUrl = () => {
    if (photos[0]) {
      const variants = getImageVariants(photos[0]);
      return variants.thumb || variants.original || '/placeholder-image.jpg';
    }
    return '/placeholder-image.jpg';
  };

  const typeIcon = getTypeIcon(type);

  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow ${highlighted ? 'ring-2 ring-blue-500' : ''} ${hovered ? 'ring-2 ring-gray-400' : ''} ${className} cursor-pointer select-none`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleCardClick}
      onTouchStart={handleTouchStartCard}
      onTouchEnd={handleTouchEndCard}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        userSelect: 'none'
      }}
    >
      {/* Image */}
      <div className="relative h-32 bg-gray-100">
        <img
          src={getImageUrl()}
          alt={title}
          className="w-full h-full object-cover pointer-events-none"
          onContextMenu={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onTouchEnd={(e) => e.preventDefault()}
          draggable={false}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/placeholder-image.jpg';
          }}
        />
        
        {/* Type Badge */}
        <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-semibold ${getTypeColor(typeIcon)}`}>
          {typeIcon}
        </div>

        {/* Save Heart Icon */}
        <button
          onClick={handleSaveClick}
          onTouchStart={handleSaveTouch}
          className={`heart-icon absolute bottom-2 right-2 p-1.5 rounded-full transition-all duration-300 z-20 ${
            isSaved 
              ? 'bg-red-500 text-white shadow-lg' 
              : 'bg-white bg-opacity-80 text-gray-600 hover:bg-opacity-100 hover:text-red-500'
          } ${isAnimating ? 'animate-pulse scale-110' : ''}`}
        >
          <Heart 
            size={16} 
            fill={isSaved ? 'currentColor' : 'none'}
            className={`transition-all duration-300 ${
              isAnimating ? 'scale-125' : ''
            }`}
          />
        </button>

        {/* Touch Overlay for Mobile - Show More Text */}
        {showOverlay && onOverlayClick && (
          <div
            className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center cursor-pointer z-10 rounded-lg"
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click
              // Don't trigger overlay if heart icon was clicked
              if (!(e.target as HTMLElement).closest('.heart-icon')) {
                onOverlayClick();
              }
            }}
            onTouchStart={(e) => {
              e.stopPropagation(); // Prevent card touch handlers
            }}
            onTouchEnd={(e) => {
              e.stopPropagation(); // Prevent card touch
              e.preventDefault(); // Prevent click event from firing
              // Don't trigger overlay if heart icon was clicked
              if (!(e.target as HTMLElement).closest('.heart-icon')) {
                onOverlayClick();
              }
            }}
          >
            <div className="bg-white bg-opacity-90 px-4 py-2 rounded-lg shadow-lg">
              <span className="text-gray-800 font-medium text-sm">Show More</span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 text-sm leading-tight">
              {truncateTitle(title)}
            </h3>
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </div>

      {/* Registration Prompt */}
      <RegistrationPrompt
        isOpen={showRegistrationPrompt}
        onClose={() => setShowRegistrationPrompt(false)}
        variant="google_auth"
        trigger="save"
      />
    </div>
  );
};

export default ChatPlaceCard;
